import { Address } from "./address";
import { Comment } from "./comment";
import { Customer } from "./customer";
import { JobShortV2 } from "./job";
import { Technician } from "./technician";

export type AppointmentTechnicianStatus =
  | "assigned"
  | "accepted"
  | "traveling"
  | "arrived"
  | "departed";

/**
 * Maps a per-technician status to the appointment-level status
 * used by shared UI components (timelines, badges).
 *
 * "traveling" collapses to "accepted" intentionally — useAppointmentTimes
 * re-derives a customization name (e.g. "traveling") from the appointment
 * via AppointmentAdapter, so the mapped status only needs to be a valid
 * AppointmentStatus that triggers the correct timeline step derivation.
 */
export function mapTechStatusToAppointmentStatus(
  techStatus: AppointmentTechnicianStatus
): AppointmentStatus {
  switch (techStatus) {
    case "assigned":
      return "pending";
    case "accepted":
    case "traveling":
      return "accepted";
    case "arrived":
      return "running";
    case "departed":
      return "completed";
    default:
      return "pending";
  }
}

export class AppointmentTechnicianEntry {
  technician_profile_id = -1;
  position = 0;
  technician_status: AppointmentTechnicianStatus = "assigned";
  accepted_time: string | null = null;
  dispatch_time: string | null = null;
  arrival_time: string | null = null;
  departure_time: string | null = null;
  pre_work_minutes: number | null = null;
  travel_minutes: number | null = null;
  work_minutes: number | null = null;
  duration_minutes: number | null = null;

  constructor(params: Partial<AppointmentTechnicianEntry> = {}) {
    Object.assign(this, params);
  }
}

export type AppointmentStatus =
  | "pending"
  | "accepted"
  | "completed"
  | "running"
  | "cancelled"
  | "lost_opportunity";

export class Appointment {
  id = -1;
  iso_date_time = "";
  scheduled_time = "";
  created_time = "";
  arrival_window_start = "";
  arrival_window_end = "";
  accepted_time = "";
  arrival_time = "";
  assignment_pinned = false;
  customer_scheduled = false;
  departure_time = "";
  dispatch_time = "";
  estimated_end_time = "";
  pre_work_minutes = null;
  total_duration_minutes = null;
  total_pre_work_minutes = null;
  total_travel_minutes = null;
  total_work_minutes = null;
  travel_minutes = null;
  work_minutes = null;
  estimated_duration_minutes = null;
  created_by = { full_name: "" };
  address = new Address();
  customer = new Customer();
  service_category = new AppointmentServiceCategory();
  status: AppointmentStatus = "pending";
  job_id = -1;
  duration_minutes = -1;
  technician?: Technician = undefined;
  links?: {
    share?: string;
  } = undefined;
  job?: {
    id: number;
    invoice_revenue_cents: number;
    status: {
      color: string;
      created_at: string;
      description: string;
      final: boolean;
      id: number;
      key: string;
      name: string;
      position: number;
      updated_at: string;
    };
  };

  constructor(params: Partial<Appointment> = {}) {
    Object.assign(this, params);
  }
}

export class AppointmentServiceCategory {
  id = -1;
  name = "";
  subtitle = "";
  options: Record<string, any> = {};
  duration = -1;
  archived: boolean;

  constructor(params: Partial<AppointmentServiceCategory> = {}) {
    Object.assign(this, params);
  }
}

export type AppointmentCreatedByUserType = "customer" | "technician" | "admin";

export class AppointmentCreatedBy {
  id = -1;
  full_name = "";
  user_type: AppointmentCreatedByUserType = "customer";

  constructor(params: Partial<AppointmentCreatedBy> = {}) {
    Object.assign(this, params);
  }
}

export class AppointmentV2 {
  id = -1;
  status: AppointmentStatus = "pending";
  created_time = "";
  scheduled_time = "";
  min_arrival_time = "";
  estimated_end_time = "";
  dispatch_time = "";
  arrival_time = "";
  departure_time = "";
  enforce_actual_times = true;
  arrival_window_minutes = 60;
  estimated_duration_minutes = 0;
  duration_minutes: number | null = null;
  travel_minutes: number | null = null;
  work_minutes: number | null = null;
  total_duration_minutes: number | null = null;
  total_travel_minutes: number | null = null;
  total_work_minutes: number | null = null;
  assignment_pinned = false;
  pinned_reason?: string | null = null;
  customer_scheduled = true;
  job = new JobShortV2();
  service_category = new AppointmentServiceCategory();
  address = new Address();
  customer = new Customer();
  technician = new Technician();
  technicians: Technician[] = [];
  appointment_technicians: AppointmentTechnicianEntry[] = [];
  created_by = new AppointmentCreatedBy();
  crew_members: any[] = []; // TODO: Define
  options: Record<string, any> = {};
  pre_work_minutes = null;
  total_pre_work_minutes = null;
  job_id?: number;
  links?: {
    share?: string;
    cancel?: string;
  } = undefined;
  iso_date_time = "";
  arrival_window_start = "";
  arrival_window_end = "";
  accepted_time = "";
  pinned_notes: { job: Comment[]; customer: Comment[] } = {
    job: [],
    customer: []
  };

  constructor(params: Partial<AppointmentV2> = {}) {
    Object.assign(this, params);
  }
}
